import React, { useMemo, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddTechnicalSanctionIcon from "@mui/icons-material/LibraryAddOutlined";
import EditIcon from "@mui/icons-material/Edit";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FeedIcon from "@mui/icons-material/Feed";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import UndoIcon from "@mui/icons-material/Undo";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import ApplicationFormPreviewModal from "Components/Common/ApplicationFormPreviewModal";
import TechnicalSanction from "pages/Admin/TechnicalSanction";
import DemandEstimation from "pages/Admin/DemandEstimation";
import ApplicationStatusHistoryChart from "Components/Charts/ApplicationStatusHistoryChart";
import Modal from "Components/Common/Modal";
import { ChartIcon } from "Components/Common/Styled/ChartIcon";
import {
  POWER_CONSUMER_APPLICATION_INCHARGE,
  SUPER_ADMIN,
  applicationTypes,
  applicationStatusTypes,
  POWER_DEPARTMENT_HEAD,
  PROCESSED_STATUS,
  APPLICATION_PROCESS_COMPLETED_STATUS,
  ESTIMATE_DEPOSITED_STATUS,
  powerAdmin3Roles,
  POWER_CONSUMER_APPLICATION_OPERATOR,
  powerAdmin2Roles,
  estimateIssued,
} from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";
import PreviewPendingDocuments from "./PreviewPendingDocuments";
import RefundBankDetailsFormModal from "./RefundBankDetailsFormModal";
import RefundCalculations from "pages/Admin/RefundCalculations";
import MeterReadingModal from "./MeterReadingModal";
import TechnicalSanctionApprovalButtons from "./TechnicalSanctionApprovalButtons";
import DemandEstimationApprovalButtons from "./DemandEstimationApprovalButtons";
import ApplicationEditModal from "Components/ApplicationEditModal";
import InputFieldText from "Components/Common/InputFieldText";
import { getOptionValue } from "common/enums";

import { getImageUrl } from "utils";
import { isAuthorizedUser } from "utils";
import getFileExtension from "utils/getFileExtension";

import {
  useLazyGetDemandEstimationByApplicationIdQuery,
  usePutDemandEstimationDocumentApproveMutation,
} from "apis/DemandEstimation";
import {
  useLazyApplicationDetailQuery,
  usePutRemoveMeterMutation,
  usePutReleaseConnectionMutation,
} from "apis/ApplicationRequestDetail";
import {
  useLazyGetTechnicalSanctionDataByIdQuery,
  usePutRevokeTechnicalSanctionMutation,
} from "apis/TechnicalSanction";
import { useLazyAdminCommentQuery } from "apis/ApplicationRequestComment";

import {
  AddEditMeterIcon,
  PreviewPendingDocumentsIcon,
  ViewDemandEstimationDocumentIcon,
  ViewTechnicalSanctionDocumentIcon,
} from "./style";

const notAllowApplicationForUpdateMeter = [
  "Name Change/Transfer",
  "Removal of Connection",
  "Backup Power",
];
const REMOVAL_OF_CONNECTION = "Removal of Connection";

const pendingDocumentNonUploadableStatus = [
  "Saved",
  "Submitted",
  "Reviewed",
  "Rejected",
];

const allowRoles = [POWER_CONSUMER_APPLICATION_INCHARGE, SUPER_ADMIN];

const allowRolesForRevokeApplication = [SUPER_ADMIN, POWER_DEPARTMENT_HEAD];

const PowerApplicationActions = ({ item, t, viewApplicationBtnHandler }) => {
  const {
    id,
    approvedApplicationDocumentURL,
    isTechnicalSanctionApproved,
    isApplicationLTPermanent,
    technicalSanctionUrl,
    demandEstimateUrl,
    applicationType,
    applicationStatus,
    isTechnicalSanctionRequired,
    isMeterAssigned,
    demandEstimateId,
    isPartialDocumentsUploaded,
    bankDetailId,
    isDemandEstimateReviewed,
    isAllPendingDocumentsApproved,
    technicalSanctionId: technicalSanctionDataId,
    isDemandEstimateDocumentApproved,
    isTechnicalFeasibility,
    isRefundCalculatedByFinance,
    technicalSanctionRevokedBy,
    isApplicationRejectedForApplicationForm,
    demandEstimateApprover,
    isTechnicalSanctionReviewed,
    // utilities,
    utilityConsumerId,
    taxInvoiceUrl,
    billOfSupplyUrl,
    isMeterRemoved,
    refundCalculationBreakupUrl,
    refundCalculationUrl,
    finalRefundAmount,
    backupkVA,
  } = item;
  const isDemandEstimationVisible =
    applicationType.name !== REMOVAL_OF_CONNECTION;
  const { roles, user: { id: userId } = {} } = useSelector(
    (state) => state.Login
  );

  const [releaseConnection, { isLoading: isConnectionReleasing }] =
    usePutReleaseConnectionMutation();

  const [revokeApplication, { isLoading: isRevokedApplicationLoading }] =
    usePutRevokeTechnicalSanctionMutation();

  const [getApplicationData, { isLoading: isApplicationDataLoading }] =
    useLazyApplicationDetailQuery();

  const [removeMeter, { isMeterRemoving }] = usePutRemoveMeterMutation();

  const [
    getTechnicalSanctionData,
    {
      isLoading: isTechnicalSanctionDataLoading,
      data: { data: { nextApproveBy, nextRevokeBy } = {} } = {},
    },
  ] = useLazyGetTechnicalSanctionDataByIdQuery();

  const [getDemandEstimation, { isLoading: isDemandEstimationDataLoading }] =
    useLazyGetDemandEstimationByApplicationIdQuery();

  const [uploadSignedDemandEstimate, { isLoading }] =
    usePutDemandEstimationDocumentApproveMutation();

  const [
    getAdminComments,
    {
      isLoading: isAdminCommentsLoading,
      data: { data: commentsData = [] } = {},
    },
  ] = useLazyAdminCommentQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const technicalSanctionId = searchParams.get("technicalSanctionId");
  const demandEstimationId = searchParams.get("demandEstimationId");

  const ref = useRef();

  const [documentToPreview, setDocumentToPreview] = useState();
  const [isApplicationFormModalOpen, setIsApplicationFormModalOpen] =
    useState(false);
  const [applicationFromData, setApplicationFromData] = useState([]);
  const [applicationIdForChartModal, setApplicationIdForChartModal] =
    useState("");
  const [isEditApplicationModalOpen, setIsEditApplicationModalOpen] =
    useState(false);
  const [isTechnicalSanctionModalOpen, setIsTechnicalSanctionModalOpen] =
    useState(false);
  const [isDemandEstimationModalOpen, setIsDemandEstimationModalOpen] =
    useState(false);
  const [
    applicationIdForMeterReadingModal,
    setApplicationIdForMeterReadingModal,
  ] = useState("");

  const [selectedApplicationDetails, setSelectedApplicationDetails] = useState(
    {}
  );
  const [utilityConsumerIdForRemoveMeter, setUtilityConsumerIdForRemoveMeter] =
    useState(false);
  const [
    consumerIdForRefundCalculationModal,
    setConsumerIdForRefundCalculationModal,
  ] = useState("");

  const [
    applicationIdForRefundPaymentInformation,
    setApplicationIdForRefundPaymentInformation,
  ] = useState("");
  const [
    consumerIdIdForRefundPaymentInformation,
    setConsumerIdForRefundPaymentInformation,
  ] = useState("");

  const [isTaxInvoiceModalOpen, setIsTaxInvoiceModalOpen] = useState(false);
  const [isBillOfSupplyDocumentModalOpen, setIsBillOfSupplyDocumentModalOpen] =
    useState(false);

  const [
    applicationIdForRefundCalculationModal,
    setApplicationIdForRefundCalculationModal,
  ] = useState("");
  const [
    isConnectionReleaseConfirmationModalOpen,
    setIsConnectionReleaseConfirmationModalOpen,
  ] = useState(false);
  const [isSubStationFieldRequired, setIsSubStationFieldRequired] =
    useState(false);
  const [partiallyUploadedDocuments, setPartiallyUploadedDocuments] = useState(
    []
  );
  const [uploadedFile, setUploadedFile] = useState("");
  const [
    isTechnicalSanctionDocumentModalOpen,
    setIsTechnicalSanctionDocumentModalOpen,
  ] = useState(false);
  const [
    isPreviewRefundCalculationBreakupModalOpen,
    setIsPreviewRefundCalculationBreakupModalOpen,
  ] = useState(false);
  const [
    isPreviewRefundCalculationModalOpen,
    setIsPreviewRefundCalculationModalOpen,
  ] = useState(false);

  const [
    isTechnicalSanctionRevokeConfirmationModalOpen,
    setIsTechnicalSanctionRevokeConfirmationModalOpen,
  ] = useState(false);

  const [
    isDemandEstimationDocumentModalOpen,
    setIsDemandEstimationDocumentModalOpen,
  ] = useState(false);

  const [isBackupPowerApplication,setIsBackUpPowerApplication] = useState(false);

  const [isSalesOrderModalOpen, setIsSalesOrderModalOpen] = useState(false);

  const handleIssueDemandEstimation = () => {
    setIsDemandEstimationModalOpen(true);
    setSelectedApplicationDetails(item);
    setIsSubStationFieldRequired(!isTechnicalSanctionRequired);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      salesOrder: "",
      isActiveBackupPower: false,
    },
    validationSchema:Yup.object({
      salesOrder: !isBackupPowerApplication ? Yup.string()
            .required("Sales order is required")
            .test(
              "isValidSalesOrder",
              "Sales order length must be 10 digits",
              (value) => /^[a-zA-Z0-9]{10}$/.test(value)
            )
        : Yup.string()}),
    onSubmit: ({ salesOrder,isActiveBackupPower }) => {
      const payload = {salesOrder,...(isBackupPowerApplication && {isActiveBackupPower})};
      releaseConnectionBtnHandler(payload);
    },
  });

  const { values, setFieldValue } = formik;

  const {
    isInProcessedStatus,
    isInEstimateDepositedStatus,
    isInEstimationIssuedStatus,
  } = useMemo(() => {
    const applicationStatusName = applicationStatus?.name?.toLowerCase();

    return {
      isInProcessedStatus: applicationStatusName === PROCESSED_STATUS,
      isInEstimateDepositedStatus:
        applicationStatusName === ESTIMATE_DEPOSITED_STATUS,
      isInApplicationProcessCompletedStatus:
        applicationStatusName === APPLICATION_PROCESS_COMPLETED_STATUS,
      isInEstimationIssuedStatus: applicationStatusName === estimateIssued,
    };
  }, [applicationStatus]);

  const isPreviewPendingDocumentsIconVisible = useMemo(
    () =>
      !pendingDocumentNonUploadableStatus.includes(applicationStatus.name) &&
      !isAllPendingDocumentsApproved &&
      isPartialDocumentsUploaded,
    [
      isPartialDocumentsUploaded,
      isAllPendingDocumentsApproved,
      applicationStatus,
    ]
  );

  const isEditButtonVisible = useMemo(
    () =>
      (isAuthorizedUser(roles, [POWER_CONSUMER_APPLICATION_INCHARGE]) &&
        [
          applicationStatusTypes["Submitted"],
          applicationStatusTypes["Reviewed"],
        ].includes(applicationStatus.id)) ||
      (roles.includes(SUPER_ADMIN) &&
        ![
          applicationStatusTypes["Saved"],
          applicationStatusTypes["Connection Released"],
          applicationStatusTypes["Application Process Completed"],
        ].includes(applicationStatus.id)),
    [applicationStatus, roles]
  );

  const isPostConnectionApplications = useMemo(
    () =>
      ![
        applicationTypes["Permanent Connection"],
        applicationTypes["Temporary Connection"],
      ].includes(applicationType?.id),
    [applicationType]
  );

  const isConnectionReleaseIconVisible = useMemo(
    () =>
      (isAuthorizedUser(roles, [...powerAdmin3Roles, SUPER_ADMIN]) &&
        ((isInEstimateDepositedStatus && isMeterAssigned) ||
          (applicationType.id === applicationTypes["Removal of Connection"] &&
            ((applicationStatus.id === applicationStatusTypes["Disconnected"] &&
              finalRefundAmount === 0 &&
              !!refundCalculationBreakupUrl &&
              isRefundCalculatedByFinance) ||
              (finalRefundAmount < 0 && isInEstimateDepositedStatus))))) ||
      (finalRefundAmount > 0 &&
        !!bankDetailId &&
        applicationStatus.id === applicationStatusTypes["Disconnected"] &&
        isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin2Roles])),
    [
      isMeterAssigned,
      isInEstimateDepositedStatus,
      finalRefundAmount,
      applicationStatus,
      bankDetailId,
      isRefundCalculatedByFinance,
      isInProcessedStatus,
      roles,
      isInEstimateDepositedStatus,
      refundCalculationBreakupUrl,
    ]
  );
  const handleUploadFile = async (event) => {
    try {
      const fileExtension = getFileExtension(
        event.target.files[0]?.name
      ).toLowerCase();
      const fileSize = event.target.files[0]?.size;
      if (fileSize > 2000000) {
        toast.error("File size should be less than 2MB");
      } else if (fileExtension !== "pdf") {
        toast.error("Invalid file format, Please upload pdf file");
      } else {
        setDocumentToPreview({
          url: URL.createObjectURL(event.target.files[0]),
          documentTypeName: "Demand Estimate",
          documentType: "pdf",
          documentName: "demandEstimate",
        });
        setUploadedFile(event.target.files[0]);
        ref.current.value = "";
      }
    } catch (err) { }
  };
  const revokeTechnicalSanctionBtnHandler = async () => {
    try {
      await revokeApplication(id);
      setIsTechnicalSanctionRevokeConfirmationModalOpen(false);
    } catch (error) { }
  };

  const releaseConnectionBtnHandler = async ({salesOrder = "",isActiveBackupPower=null}) => {
    try {
      await releaseConnection({
        applicationId: id,
        query:{...(!!salesOrder.length && { salesOrder }),...(isActiveBackupPower!==null && {isActiveBackupPower})},
        toastMessage: [
          applicationTypes["Permanent Connection"],
          applicationTypes["Temporary Connection"],
        ].includes(applicationType.id)
          ? "Connection released"
          : "Application process completed",
      });
      setIsConnectionReleaseConfirmationModalOpen(false);
      setIsSalesOrderModalOpen(false);
    } catch (error) { }
  };

  const uploadSignedDemandEstimateHandler = async () => {
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("applicationIds[0]", id);
      formData.append("isApproved", true);

      await uploadSignedDemandEstimate(formData);

      setUploadedFile("");
      setDocumentToPreview(null);
    } catch (error) { }
  };

  const previewDocumentsBtnHandler = async (applicationId) => {
    const { data: { documents = [] } = {} } = await getApplicationData(
      applicationId
    );
    const optionalDocuments = documents.filter(({ isApproved }) => !isApproved);
    if (optionalDocuments.length) {
      setPartiallyUploadedDocuments(optionalDocuments);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (
          technicalSanctionId &&
          +technicalSanctionId === technicalSanctionDataId
        ) {
          const { data: { url } = {} } = await getTechnicalSanctionData(
            technicalSanctionId
          );
          await getAdminComments({
            commentType: getOptionValue("adminComments", "TechnicalSanction"),
            commentTypeId: technicalSanctionId,
          });
          setIsTechnicalSanctionDocumentModalOpen(true);
          setDocumentToPreview({
            url: getImageUrl(url),
            documentTypeName: "Technical Sanction Document",
            documentType: "pdf",
            documentName: "technicalSanction",
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [technicalSanctionId, technicalSanctionDataId]);

  useEffect(() => {
    (async () => {
      try {
        if (demandEstimationId && +demandEstimationId === demandEstimateId) {
          const { data: { url } = {} } = await getDemandEstimation(id);
          setIsDemandEstimationDocumentModalOpen(true);
          setDocumentToPreview({
            url: getImageUrl(url),
            documentTypeName: "Demand Estimation Document",
            documentType: "pdf",
            documentName: "demandEstimation",
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [demandEstimationId, demandEstimateId]);

  return (
    <Box width="150px">
      <Backdrop
        open={
          isApplicationDataLoading ||
          isTechnicalSanctionDataLoading ||
          isDemandEstimationDataLoading ||
          isAdminCommentsLoading
        }
      />

      {isSalesOrderModalOpen && (
        <Modal
          isOpen
          handleClose={() => {
            setIsSalesOrderModalOpen(false);
            formik.resetForm();
          }}
          title="Add Sales Order Number"
        >
          <div className="mt-3">
            <InputFieldText
              formik={formik}
              name="salesOrder"
              label={t("Sales Order") + (isBackupPowerApplication ? "" : "*")}
              placeholder={t("Enter Sales Order")}
            />
            {isBackupPowerApplication && 
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={values.isActiveBackupPower}
                    onChange={(e) =>
                      setFieldValue("isActiveBackupPower", e.target.checked)
                    }
                  />
                }
                label={t("Active Backup Power")}
              />
            }
            <Button
              onClick={formik.handleSubmit}
              variant="contained"
              className="d-flex ms-auto gap-2"
              disabled={isLoading || isConnectionReleasing}
            >
              {t("Submit")}
              {(isLoading || isConnectionReleasing) && (
                <CircularProgress size={20} />
              )}
            </Button>
          </div>
        </Modal>
      )}

      {!!partiallyUploadedDocuments.length && (
        <Modal
          isOpen
          handleClose={() => {
            setPartiallyUploadedDocuments([]);
          }}
          title="Documents"
          maxWidth="md"
        >
          <PreviewPendingDocuments
            documents={partiallyUploadedDocuments}
            applicationId={id}
            handleClose={() => {
              setPartiallyUploadedDocuments([]);
            }}
          />
        </Modal>
      )}

      {isEditApplicationModalOpen && (
        <ApplicationEditModal
          applicationTypeId={applicationType.id}
          applicationId={id}
          handleClose={() => setIsEditApplicationModalOpen(false)}
        />
      )}

      {!!applicationIdForChartModal && (
        <Modal
          isOpen
          handleClose={() => setApplicationIdForChartModal("")}
          title="Application Status History Chart"
          maxWidth="md"
        >
          <ApplicationStatusHistoryChart
            applicationId={applicationIdForChartModal}
          />
        </Modal>
      )}
      <ConfirmationDialog
        isOpen={utilityConsumerIdForRemoveMeter}
        title="Warning"
        message="Are you sure you want to remove this meter?"
        onCancel={() => setUtilityConsumerIdForRemoveMeter("")}
        onConfirm={async () => {
          try {
            await removeMeter(utilityConsumerIdForRemoveMeter, {
              isMeterRemoved: true,
              isForApplication: true,
            });
            setUtilityConsumerIdForRemoveMeter("");
          } catch (error) { }
        }}
        isLoading={isMeterRemoving}
        variant="warning"
      />
      <ConfirmationDialog
        isOpen={isTechnicalSanctionRevokeConfirmationModalOpen}
        title="Warning"
        message="Are you sure you want to revoke this application?"
        onCancel={() =>
          setIsTechnicalSanctionRevokeConfirmationModalOpen(false)
        }
        onConfirm={revokeTechnicalSanctionBtnHandler}
        isLoading={isRevokedApplicationLoading}
        variant="warning"
      />
      <ConfirmationDialog
        isOpen={isConnectionReleaseConfirmationModalOpen}
        title="Warning"
        message="Are you sure you want to complete this application?"
        onCancel={() => setIsConnectionReleaseConfirmationModalOpen(false)}
        onConfirm={releaseConnectionBtnHandler}
        isLoading={isConnectionReleasing}
        variant="warning"
      />
      {isApplicationFormModalOpen && (
        <ApplicationFormPreviewModal
          url={getImageUrl(applicationFromData?.approvedApplicationDocumentURL)}
          applicationType={applicationFromData?.applicationType?.name}
          applicationId={applicationFromData?.id}
          utilities={[1]}
          applicationStatus={applicationFromData?.applicationStatus?.name}
          openApplicationForm={isApplicationFormModalOpen}
          handleClose={() => setIsApplicationFormModalOpen(false)}
        />
      )}
      {isTechnicalSanctionModalOpen && (
        <TechnicalSanction
          isTechnicalSanctionModalOpen={isTechnicalSanctionModalOpen}
          handleClose={() => {
            setIsTechnicalSanctionModalOpen(false);
            setSelectedApplicationDetails("");
          }}
          selectedApplicationDetails={selectedApplicationDetails}
        />
      )}
      {isDemandEstimationModalOpen && (
        <DemandEstimation
          isDemandEstimationModalOpen={isDemandEstimationModalOpen}
          applicationId={selectedApplicationDetails?.id}
          applicationTypeId={selectedApplicationDetails?.applicationType?.id}
          handleClose={() => {
            setIsDemandEstimationModalOpen(false);
            setSelectedApplicationDetails("");
          }}
          isSubStationFieldRequired={isSubStationFieldRequired}
        />
      )}
      {!!applicationIdForMeterReadingModal && (
        <MeterReadingModal
          applicationTypeId={applicationType.id}
          applicationId={applicationIdForMeterReadingModal}
          isSolarMeter={applicationType.id === applicationTypes["Net Metering"]}
          handleClose={() => setApplicationIdForMeterReadingModal("")}
        />
      )}
      {!!consumerIdForRefundCalculationModal && (
        <RefundCalculations
          utilityConsumerId={consumerIdForRefundCalculationModal}
          applicationId={applicationIdForRefundCalculationModal}
          handleClose={() => {
            setConsumerIdForRefundCalculationModal("");
            setApplicationIdForRefundCalculationModal("");
          }}
        />
      )}
      {!!applicationIdForRefundPaymentInformation && (
        <RefundBankDetailsFormModal
          utilityConsumerId={consumerIdIdForRefundPaymentInformation}
          applicationId={applicationIdForRefundPaymentInformation}
          handleClose={() => {
            setApplicationIdForRefundPaymentInformation("");
            setConsumerIdForRefundPaymentInformation("");
          }}
        />
      )}
      {isTechnicalSanctionDocumentModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsTechnicalSanctionDocumentModalOpen(false);
            setDocumentToPreview(null);
            searchParams.delete("technicalSanctionId");
            setSearchParams(searchParams);
          }}
          actionButton={
            <TechnicalSanctionApprovalButtons
              technicalSanctionData={{
                nextApproveBy,
                nextRevokeBy,
                applicationId: id,
                technicalSanctionDataId,
              }}
              isTechnicalSanctionApproved={isTechnicalSanctionApproved}
              handleClose={() => {
                setIsTechnicalSanctionDocumentModalOpen(false);
                setDocumentToPreview(null);
                searchParams.delete("technicalSanctionId");
                setSearchParams(searchParams);
              }}
              commentsData={commentsData}
            />
          }
          {...documentToPreview}
        />
      )}

      {!!documentToPreview && isTaxInvoiceModalOpen && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsTaxInvoiceModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {!!documentToPreview && !!uploadedFile && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setUploadedFile("");
            setDocumentToPreview(null);
          }}
          actionButton={
            <Button
              onClick={uploadSignedDemandEstimateHandler}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      {!!documentToPreview && isBillOfSupplyDocumentModalOpen && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsBillOfSupplyDocumentModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {isDemandEstimationDocumentModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsDemandEstimationDocumentModalOpen(false);
            setSelectedApplicationDetails("");
            setDocumentToPreview(null);
            searchParams.delete("demandEstimationId");
            setSearchParams(searchParams);
          }}
          actionButton={
            !!demandEstimateUrl &&
            !isDemandEstimateReviewed &&
            demandEstimateApprover?.userId?.includes(userId) && (
              <DemandEstimationApprovalButtons
                DemandEstimationData={{
                  applicationId: id,
                }}
                handleClose={() => {
                  setIsDemandEstimationDocumentModalOpen(false);
                  setDocumentToPreview(null);
                  searchParams.delete("demandEstimationId");
                  setSearchParams(searchParams);
                }}
              />
            )
          }
          {...documentToPreview}
        />
      )}
      {isPreviewRefundCalculationBreakupModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsPreviewRefundCalculationBreakupModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {isPreviewRefundCalculationModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsPreviewRefundCalculationModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}
      {/* View icon for application */}
      <IconButton
        onClick={() => viewApplicationBtnHandler(id, applicationType.name)}
      >
        <Tooltip title={t("View Application Details")}>
          <RemoveRedEyeIcon
            fontSize="small"
            color="primary"
            className="cursor-pointer"
          />
        </Tooltip>
      </IconButton>
      {/* Preview icon to view  uploaded application document */}
      {!!approvedApplicationDocumentURL && (
        <IconButton
          onClick={() => {
            setIsApplicationFormModalOpen(!!approvedApplicationDocumentURL);
            setApplicationFromData(item);
          }}
        >
          <Tooltip title={t("View Application Form")}>
            <PreviewIcon
              fontSize="small"
              color={approvedApplicationDocumentURL ? "primary" : "disabled"}
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      )}

      {isEditButtonVisible && (
        <IconButton onClick={() => setIsEditApplicationModalOpen(true)}>
          <Tooltip title={t("Edit Application Form")}>
            <EditIcon fontSize="small" color="primary" />
          </Tooltip>
        </IconButton>
      )}

      {/* Generate technical sanction*/}
      {(isInProcessedStatus ||
        (isApplicationLTPermanent && isInEstimationIssuedStatus)) &&
        isAuthorizedUser(roles, allowRoles) &&
        isTechnicalSanctionRequired &&
        !isTechnicalSanctionApproved &&
        (!!technicalSanctionRevokedBy || !isTechnicalSanctionReviewed) && (
          <IconButton
            onClick={() => {
              setIsTechnicalSanctionModalOpen(true);
              setSelectedApplicationDetails(item);
            }}
          >
            <Tooltip title={t("Generate Technical Sanction")}>
              <AddTechnicalSanctionIcon
                fontSize="small"
                className="cursor-pointer"
                color="primary"
              />
            </Tooltip>
          </IconButton>
        )}
      {!!technicalSanctionUrl && (
        <IconButton
          onClick={() => {
            searchParams.set("technicalSanctionId", technicalSanctionDataId);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Technical Sanction Document")}>
            <ViewTechnicalSanctionDocumentIcon />
          </Tooltip>
        </IconButton>
      )}
      {isDemandEstimationVisible &&
        isInProcessedStatus &&
        (isTechnicalSanctionApproved ||
          (isApplicationLTPermanent && !!technicalSanctionUrl)) &&
        (!demandEstimateApprover ||
          powerAdmin3Roles.includes(demandEstimateApprover?.roleName)) &&
        isAuthorizedUser(roles, allowRoles) && (
          <IconButton onClick={handleIssueDemandEstimation}>
            <Tooltip title={t("Issue Demand Estimation")}>
              <CalculateIcon
                fontSize="small"
                className="cursor-pointer"
                color="primary"
              />
            </Tooltip>
          </IconButton>
        )}
      {/* Preview icon to view demand estimation document */}
      {isDemandEstimationVisible && !!demandEstimateUrl && (
        <IconButton
          onClick={() => {
            setIsDemandEstimationDocumentModalOpen(true);
            searchParams.set("demandEstimationId", demandEstimateId);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Demand Estimation Document")}>
            <ViewDemandEstimationDocumentIcon />
          </Tooltip>
        </IconButton>
      )}
      {isAuthorizedUser(roles, [
        POWER_CONSUMER_APPLICATION_INCHARGE,
        POWER_CONSUMER_APPLICATION_OPERATOR,
      ]) &&
        isDemandEstimateReviewed &&
        !isDemandEstimateDocumentApproved && (
          <IconButton size="small" color="primary" component="label">
            <Tooltip title={t("Upload Signed Demand Estimate")}>
              <FileUploadIcon />
              <input
                ref={ref}
                style={{
                  display: "none",
                }}
                accept=".pdf"
                type="file"
                onChange={(e) => handleUploadFile(e)}
              />
            </Tooltip>
          </IconButton>
        )}

      {isPreviewPendingDocumentsIconVisible && (
        <IconButton onClick={() => previewDocumentsBtnHandler(id)}>
          <Tooltip title={t("Preview Partially Uploaded Documents")}>
            <PreviewPendingDocumentsIcon />
          </Tooltip>
        </IconButton>
      )}

      {!!billOfSupplyUrl && (
        <Tooltip title={t("Bill Of Supply")}>
          <IconButton
            onClick={() => {
              setDocumentToPreview({
                url: getImageUrl(billOfSupplyUrl),
                documentTypeName: "Bill Of Supply Document",
                documentType: "pdf",
                documentName: "Bill Of Supply",
              });
              setIsBillOfSupplyDocumentModalOpen(true);
            }}
            size="small"
          >
            <FileCopyIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {!!taxInvoiceUrl && (
        <Tooltip title={t("Tax Invoice")}>
          <IconButton
            onClick={() => {
              setDocumentToPreview({
                url: getImageUrl(taxInvoiceUrl),
                documentTypeName: "Tax Invoice Document",
                documentType: "pdf",
                documentName: "Tax Invoice",
              });
              setIsTaxInvoiceModalOpen(true);
            }}
            size="small"
          >
            <ReceiptIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {/* Add or Update meter information */}
      {((isInEstimateDepositedStatus &&
        !notAllowApplicationForUpdateMeter.includes(applicationType.name)) ||
        (isInProcessedStatus &&
          applicationType.id === applicationTypes["Removal of Connection"] &&
          isAuthorizedUser(roles, powerAdmin3Roles) &&
          !isMeterRemoved)) && (
        <IconButton onClick={() => setApplicationIdForMeterReadingModal(id)}>
          <Tooltip title={t("Add/Update Meter Information")}>
            <AddEditMeterIcon />
          </Tooltip>
        </IconButton>
      )}

      {!!isInProcessedStatus &&
        applicationType.id === applicationTypes["Removal of Connection"] &&
        !isMeterRemoved &&
        isMeterAssigned && (
          <Tooltip title={t("Remove Meter")}>
            <IconButton
              onClick={() =>
                setUtilityConsumerIdForRemoveMeter(utilityConsumerId)
              }
              size="small"
            >
              <CancelPresentationIcon color="primary" />
            </IconButton>
          </Tooltip>
        )}

      {applicationStatus.id === applicationStatusTypes["Disconnected"] &&
        applicationType.id === applicationTypes["Removal of Connection"] &&
        isMeterRemoved &&
        ((isAuthorizedUser(roles, powerAdmin3Roles) &&
          !refundCalculationBreakupUrl) ||
          ((roles.includes(SUPER_ADMIN) ||
            isAuthorizedUser(roles, powerAdmin2Roles)) &&
            refundCalculationBreakupUrl)) && (
          <Tooltip title={t("Refund Calculations")}>
            <IconButton
              onClick={() => {
                setConsumerIdForRefundCalculationModal(utilityConsumerId);
                setApplicationIdForRefundCalculationModal(id);
              }}
              size="small"
            >
              <FeedIcon color="primary" />
            </IconButton>
          </Tooltip>
        )}

      {!!refundCalculationBreakupUrl && (
        <Tooltip title={t("Refund Calculations Breakup Document")}>
          <IconButton
            onClick={() => {
              setIsPreviewRefundCalculationBreakupModalOpen(true);
              setDocumentToPreview({
                url: getImageUrl(refundCalculationBreakupUrl),
                documentTypeName: "Refund Calculations Breakup Document",
                documentType: "pdf",
                documentName: "Refund Calculations Breakup",
              });
            }}
            size="small"
          >
            <RequestPageIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {!!refundCalculationUrl && (
        <Tooltip title={t("Refund Calculations Document")}>
          <IconButton
            onClick={() => {
              setIsPreviewRefundCalculationModalOpen(true);
              setDocumentToPreview({
                url: getImageUrl(refundCalculationUrl),
                documentTypeName: "Refund Calculations",
                documentType: "pdf",
                documentName: "Refund Calculations",
              });
            }}
            size="small"
          >
            <InsertDriveFileIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}
      {/* Revoke Application */}
      {((applicationStatus.id === applicationStatusTypes["Rejected"] &&
        (!isTechnicalFeasibility || isApplicationRejectedForApplicationForm)) ||
        applicationStatus.id === applicationStatusTypes["Expired"]) &&
        isAuthorizedUser(roles, allowRolesForRevokeApplication) && (
          <IconButton
            onClick={() =>
              setIsTechnicalSanctionRevokeConfirmationModalOpen(true)
            }
          >
            <Tooltip title={t("Revoke Application")}>
              <UndoIcon
                fontSize="small"
                className="cursor-pointer"
                color="primary"
              />
            </Tooltip>
          </IconButton>
        )}
      {/* Release Connection  */}
      {isConnectionReleaseIconVisible && (
        <IconButton
          onClick={() => {
            if (
              applicationType.id ===
                applicationTypes["Removal of Connection"] &&
              finalRefundAmount > 0
            ) {
              setApplicationIdForRefundPaymentInformation(id);
              setConsumerIdForRefundPaymentInformation(utilityConsumerId);
            } else if (
              ((applicationType.id ===
                applicationTypes["Permanent Connection"] ||
                applicationType.id ===
                  applicationTypes["Temporary Connection"]) &&
                backupkVA > 0) || applicationType.id ===
              applicationTypes["Backup Power"]
            ) {
              setIsSalesOrderModalOpen(true);
              if(applicationType.id === applicationTypes["Backup Power"]){
                setIsBackUpPowerApplication(true);
              }
            } else {
              setIsConnectionReleaseConfirmationModalOpen(true);
            }
          }}
        >
          <Tooltip
            title={t(
              isPostConnectionApplications
                ? "Application Process Completion"
                : "Release Connection"
            )}
          >
            <CheckCircleIcon
              fontSize="small"
              className="cursor-pointer"
              color="success"
            />
          </Tooltip>
        </IconButton>
      )}
      {/* view icon for history chart for application status  */}
      <IconButton onClick={() => setApplicationIdForChartModal(id)}>
        <Tooltip title={t("Application Status History Chart")}>
          <ChartIcon />
        </Tooltip>
      </IconButton>
    </Box>
  );
};

PowerApplicationActions.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func,
  viewApplicationBtnHandler: PropTypes.func,
};

export default withTranslation()(PowerApplicationActions);
